import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type FC,
  type KeyboardEvent as ReactKeyboardEvent,
  type SVGProps,
} from "react";
import { createPortal } from "react-dom";
import CloseIcon from "../../assets/svg/close-icon";
import ChevronIcon from "../../assets/svg/chevron-icon";
import CheckIcon from "../../assets/svg/check-icon";
import Button from "../Button/Button";
import CalendarIcon from "../../assets/svg/calendar-icon";
import SingleDatePicker from "../SingleDatePicker/SingleDatePicker";
import { DurationPopperProps } from "./types";
import {
  DEFAULT_DURATION_OPTIONS,
  type DurationOption,
} from "./utils/constants";
import { assignDatesToDuration } from "./utils/dateUtils";
import { format, endOfDay } from "date-fns";

const FALLBACK_PRESET_ID = "last-30-days";
const PANEL_HEIGHT_FALLBACK = 480;
const PORTAL_Z_OVERLAY = 9999998;
const PORTAL_Z_PANEL = 9999999;

function resolveDefaultPresetId(requested?: string): string {
  const id = requested?.trim() || FALLBACK_PRESET_ID;
  return assignDatesToDuration(id) ? id : FALLBACK_PRESET_ID;
}

function deriveDraftFromProps(
  initialDates: { from: Date; to: Date } | undefined,
  initialOption: DurationOption | undefined,
  defaultPresetId: string
): {
  from: Date | null;
  to: Date | null;
  selectedOption: string | null;
} {
  if (initialDates?.from && initialDates?.to) {
    return {
      from: initialDates.from,
      to: initialDates.to,
      selectedOption: initialOption?.id ?? "custom",
    };
  }
  if (initialOption?.id) {
    const range = assignDatesToDuration(initialOption.id);
    if (range) {
      return {
        from: range.from,
        to: range.to,
        selectedOption: initialOption.id,
      };
    }
    return {
      from: null,
      to: null,
      selectedOption: initialOption.id,
    };
  }
  const resolved = resolveDefaultPresetId(defaultPresetId);
  const fallback = assignDatesToDuration(resolved);
  return {
    from: fallback?.from ?? null,
    to: fallback?.to ?? null,
    selectedOption: resolved,
  };
}

const PANEL_WIDTH_CLASS = "w-[390px] max-w-[390px]";

const DurationPopper = ({
  variant = "default",
  isOpen,
  onClose,
  onOpen,
  durationOptions = [...DEFAULT_DURATION_OPTIONS],
  onApply,
  title = "Duration",
  triggerName = "Select Duration",
  disabled = false,
  className = "",
  triggerClassName = "",
  triggerIconClassName = "",
  selectedOptionContainerClassName = "",
  initialDates,
  initialOption,
  defaultPresetId: defaultPresetIdProp,
  applyOnMount = true,
  usePortal = true,
  portalOffsetGap = 4,
  portalHorizontalAlign = "start",
  portalOffsetX = 0,
  panelClassName = "",
  overlayClassName = "",
  triggerAriaLabel = "Select duration",
  triggerTextClassName = "",
  datePickerIconType = "chevron",
}: DurationPopperProps) => {
  const resolvedDefaultPresetId = resolveDefaultPresetId(defaultPresetIdProp);

  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(
    () =>
      deriveDraftFromProps(initialDates, initialOption, resolvedDefaultPresetId)
        .from
  );
  const [selectedToDate, setSelectedToDate] = useState<Date | null>(
    () =>
      deriveDraftFromProps(initialDates, initialOption, resolvedDefaultPresetId)
        .to
  );
  const [openCalendar, setOpenCalendar] = useState<"from" | "to" | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(
    () =>
      deriveDraftFromProps(initialDates, initialOption, resolvedDefaultPresetId)
        .selectedOption
  );

  const [appliedDisplay, setAppliedDisplay] = useState(() =>
    deriveDraftFromProps(initialDates, initialOption, resolvedDefaultPresetId)
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const [portalPosition, setPortalPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  }>({ left: 0, width: 390 });

  const effectiveUsePortal = variant === "default" && usePortal !== false;

  const applyDraftFromProps = useCallback(() => {
    const d = deriveDraftFromProps(
      initialDates,
      initialOption,
      resolvedDefaultPresetId
    );
    setSelectedFromDate(d.from);
    setSelectedToDate(d.to);
    setSelectedOption(d.selectedOption);
  }, [initialDates, initialOption, resolvedDefaultPresetId]);

  useEffect(() => {
    setAppliedDisplay(
      deriveDraftFromProps(initialDates, initialOption, resolvedDefaultPresetId)
    );
  }, [initialDates, initialOption, resolvedDefaultPresetId]);

  useEffect(() => {
    if (variant === "inline" || !applyOnMount) return;
    if (
      !initialDates &&
      !initialOption &&
      onApply &&
      selectedFromDate &&
      selectedToDate &&
      selectedOption
    ) {
      const selectedDurationOption = durationOptions.find(
        (opt) => opt.id === selectedOption
      );
      onApply(selectedFromDate, selectedToDate, selectedDurationOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (variant !== "default") return;
    if (isOpen !== wasOpenRef.current) {
      applyDraftFromProps();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, variant, applyDraftFromProps]);

  const updatePortalPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = portalOffsetGap;
    const width = Math.max(
      panelRef.current?.offsetWidth ?? 390,
      Math.min(390, window.innerWidth - 16)
    );
    const panelH =
      panelRef.current && panelRef.current.offsetHeight > 0
        ? panelRef.current.offsetHeight
        : PANEL_HEIGHT_FALLBACK;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openAbove = spaceBelow < panelH && spaceAbove > spaceBelow;

    let left: number;
    switch (portalHorizontalAlign) {
      case "end":
        left = rect.right - width;
        break;
      case "center":
        left = rect.left + rect.width / 2 - width / 2;
        break;
      default:
        left = rect.left;
    }
    left += portalOffsetX;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));

    setPortalPosition(
      openAbove
        ? {
            bottom: window.innerHeight - rect.top + gap,
            left,
            width,
          }
        : {
            top: rect.bottom + gap,
            left,
            width,
          }
    );
  }, [portalOffsetGap, portalHorizontalAlign, portalOffsetX]);

  useEffect(() => {
    if (!isOpen || !effectiveUsePortal) return;
    updatePortalPosition();
    const raf = requestAnimationFrame(() => updatePortalPosition());
    window.addEventListener("resize", updatePortalPosition);
    window.addEventListener("scroll", updatePortalPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updatePortalPosition);
      window.removeEventListener("scroll", updatePortalPosition, true);
    };
  }, [isOpen, effectiveUsePortal, updatePortalPosition]);

  useEffect(() => {
    if (!isOpen || !effectiveUsePortal) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, effectiveUsePortal, onClose]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      if (!isOpen) {
        onOpen();
      } else {
        onClose();
      }
    }
  }, [disabled, isOpen, onOpen, onClose]);

  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case "Enter":
        case " ":
          event.preventDefault();
          handleToggle();
          break;
        case "Escape":
          if (isOpen) {
            onClose();
          }
          break;
      }
    },
    [disabled, isOpen, handleToggle, onClose]
  );

  useEffect(() => {
    if (variant === "inline" || effectiveUsePortal) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (
        !target.closest(".duration-popper") &&
        !target.closest(".duration-trigger")
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, variant, effectiveUsePortal]);

  const handleOverlayPointerDown = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOptionSelect = (optionId: string) => {
    if (optionId === "custom") {
      setSelectedOption(optionId);
      return;
    }
    const dates = assignDatesToDuration(optionId);
    if (dates) {
      setSelectedFromDate(dates.from);
      setSelectedToDate(dates.to);
      setSelectedOption(optionId);
    }
  };

  const handleDateChange = (date: Date | null, isFromDate: boolean) => {
    const now = new Date();

    if (date && date > now) {
      return;
    }

    if (isFromDate) {
      if (date && selectedToDate && date > selectedToDate) {
        return;
      }
      setSelectedFromDate(date);
    } else {
      if (date && selectedFromDate && date < selectedFromDate) {
        return;
      }
      const endOfDayDate = date ? endOfDay(date) : null;
      setSelectedToDate(endOfDayDate);
    }

    setSelectedOption("custom");
  };

  const handleApply = () => {
    if (onApply && selectedFromDate && selectedToDate) {
      const selectedDurationOption = durationOptions.find(
        (opt) => opt.id === selectedOption
      );
      setAppliedDisplay({
        from: selectedFromDate,
        to: selectedToDate,
        selectedOption,
      });
      onApply(selectedFromDate, selectedToDate, selectedDurationOption);
    }
    onClose();
  };

  const handleCancel = () => {
    applyDraftFromProps();
    onClose();
  };

  const rangeInvalid = Boolean(
    selectedFromDate && selectedToDate && selectedFromDate > selectedToDate
  );

  const defaultPickerLabel =
    "text-md font-normal text-verba-field-label dark:text-verba-field-label-dk leading-[16px]";
  const defaultPickerInputBase =
    "h-[36px] min-h-[36px] text-md font-normal text-heading-text dark:text-heading-text-dk bg-pure-white dark:bg-pure-white-dk border px-[10px] rounded-[8px] border-border-gray dark:border-border-gray-dk";
  const defaultPickerIcon =
    "w-[14px] h-[14px] shrink-0 text-heading-text dark:text-heading-text-dk";

  const DatePickerIcon =
    datePickerIconType === "calendar"
      ? (CalendarIcon as FC<SVGProps<SVGSVGElement>>)
      : datePickerIconType === "chevron"
        ? (ChevronIcon as FC<SVGProps<SVGSVGElement>>)
        : undefined;

  const renderPresetCheck = () => (
    <CheckIcon className="size-[12px] shrink-0 text-verba-blue dark:text-verba-blue-dk" />
  );

  const renderTrigger = () => {
    let displayText = triggerName;
    if (appliedDisplay.selectedOption && appliedDisplay.selectedOption !== "custom") {
      const option = durationOptions.find((opt) => opt.id === appliedDisplay.selectedOption);
      displayText = option?.label || triggerName;
    } else if (appliedDisplay.from && appliedDisplay.to) {
      displayText = `${format(appliedDisplay.from, "dd MMM yyyy")} to ${format(
        appliedDisplay.to,
        "dd MMM yyyy"
      )}`;
    }

    return (
      <div
        className={`flex h-full min-h-0 min-w-0 flex-1 items-center gap-[4px] justify-between px-2 py-0 ${selectedOptionContainerClassName}`}
      >
        <span
          className={`text-md font-medium text-heading-text dark:text-heading-text-dk overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0 pr-1 ${triggerTextClassName}`}
        >
          {displayText}
        </span>
        <ChevronIcon
          className={`w-[14px] h-[14px] shrink-0 text-verba-indigo dark:text-verba-indigo-dk transition-transform ${triggerIconClassName} ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>
    );
  };

  const chipBase =
    "h-[26px] min-h-[26px] inline-flex items-center justify-center gap-[4px] rounded-[6px] px-[8px] py-[5px] text-md transition-colors duration-200";
  const chipIdle = `${chipBase} font-normal text-heading-text dark:text-heading-text-dk bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk`;
  const chipActive = `${chipBase} font-medium text-verba-blue dark:text-verba-blue-dk bg-pure-white dark:bg-pure-white-dk border border-verba-blue dark:border-verba-blue-dk`;

  const panelLayoutClass =
    "duration-popper box-border flex flex-col gap-[24px] bg-popper-bg/80 dark:bg-popper-bg-dk/80 rounded-[8px] px-[18px] pt-[18px] pb-[24px] shadow-popper-blur-effect border border-solid border-border-gray dark:border-border-gray-dk backdrop-blur-[20px]";

  const shellForVariant =
    variant === "inline"
      ? `${panelLayoutClass} w-full z-[1] ${panelClassName}`
      : effectiveUsePortal
        ? `${panelLayoutClass} ${PANEL_WIDTH_CLASS} ${panelClassName}`
        : `${panelLayoutClass} absolute top-full left-0 mt-1 ${PANEL_WIDTH_CLASS} z-[9999] ${panelClassName}`;

  const applyDisabled = !selectedFromDate || !selectedToDate || rangeInvalid;

  const panelBody = (
    <>
      <div className="flex items-center justify-between w-full">
        <h3 className="text-lg font-semibold text-verba-indigo dark:text-verba-indigo-dk leading-none shrink-0">
          {title}
        </h3>
        {variant === "default" && (
          <button
            type="button"
            className="relative flex size-[18px] shrink-0 items-center justify-center rounded-full bg-tag-gray dark:bg-tag-gray-dk transition-transform duration-200 hover:scale-110"
            aria-label="Close modal"
            onClick={onClose}
          >
            <CloseIcon
              className="w-2 h-2 shrink-0 text-verba-indigo dark:text-verba-indigo-dk"
              strokeWidth={1}
            />
          </button>
        )}
      </div>

      <div className="flex w-full flex-wrap content-start items-start gap-x-[8px] gap-y-[12px] pr-2">
        {durationOptions.map((option) => (
          <button
            key={option.id}
            type="button"
            className={selectedOption === option.id ? chipActive : chipIdle}
            onClick={() => handleOptionSelect(option.id)}
          >
            {selectedOption === option.id &&
              option.id !== "custom" &&
              renderPresetCheck()}
            {option.label}
          </button>
        ))}
      </div>

      <hr className="w-full border-0 border-t border-solid border-border-gray dark:border-border-gray-dk" />

      <div className="flex w-full items-start gap-[18px]">
        <SingleDatePicker
          label="From"
          labelClassName={defaultPickerLabel}
          Icon={DatePickerIcon}
          iconLeft
          iconClassName={defaultPickerIcon}
          value={selectedFromDate}
          onChange={(date) => handleDateChange(date, true)}
          containerClassName="w-full !gap-[6px]"
          maxDate={selectedToDate ? new Date(selectedToDate) : new Date()}
          isOpen={openCalendar === "from"}
          onOpenChange={(openPicker) =>
            setOpenCalendar(openPicker ? "from" : null)
          }
          placeholder="Select"
          inputClassName={`${defaultPickerInputBase} ${
            rangeInvalid
              ? "border-verba-red-alert dark:border-verba-red-alert-dk"
              : ""
          }`}
        />
        <SingleDatePicker
          label="To"
          labelClassName={defaultPickerLabel}
          Icon={DatePickerIcon}
          iconLeft
          iconClassName={defaultPickerIcon}
          value={selectedToDate}
          onChange={(date) => handleDateChange(date, false)}
          containerClassName="w-full !gap-[6px]"
          minDate={selectedFromDate ? new Date(selectedFromDate) : undefined}
          maxDate={new Date()}
          isOpen={openCalendar === "to"}
          onOpenChange={(openPicker) =>
            setOpenCalendar(openPicker ? "to" : null)
          }
          placeholder="Select"
          inputClassName={`${defaultPickerInputBase} ${
            rangeInvalid
              ? "border-verba-red-alert dark:border-verba-red-alert-dk"
              : ""
          }`}
        />
      </div>

      <div className="flex w-full items-center justify-end gap-[8px]">
        <Button
          innerText="Cancel"
          onClick={handleCancel}
          buttonContainerClass="h-[26px] min-h-[26px] w-[120px] min-w-[120px] rounded-[8px] border border-solid border-verba-indigo dark:border-verba-indigo-dk bg-transparent px-[12px] py-[8px] text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk"
        />
        <Button
          innerText="Apply"
          onClick={handleApply}
          disabled={applyDisabled}
          buttonContainerClass="h-[26px] min-h-[26px] w-[120px] min-w-[120px] rounded-[8px] border border-solid border-verba-indigo dark:border-verba-indigo-dk bg-verba-indigo dark:bg-verba-indigo-dk px-[12px] py-[8px] text-md font-semibold text-pure-white disabled:cursor-not-allowed disabled:opacity-50 dark:text-pure-white-dk"
        />
      </div>
    </>
  );

  const closeDatePicker = useCallback(() => {
    setOpenCalendar(null);
  }, []);

  const panelNode =
    variant === "inline" ? (
      <div className={shellForVariant} onMouseDown={closeDatePicker}>
        {panelBody}
      </div>
    ) : effectiveUsePortal ? (
      <div
        ref={panelRef}
        className={shellForVariant}
        style={{
          position: "fixed",
          ...(portalPosition.bottom !== undefined
            ? { bottom: portalPosition.bottom }
            : { top: portalPosition.top }),
          left: portalPosition.left,
          width: portalPosition.width,
          zIndex: PORTAL_Z_PANEL,
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          closeDatePicker();
        }}
      >
        {panelBody}
      </div>
    ) : (
      <div className={shellForVariant} ref={panelRef} onMouseDown={closeDatePicker}>
        {panelBody}
      </div>
    );

  const portalSubtree =
    isOpen && variant === "default" && effectiveUsePortal ? (
      <>
        <div
          className={`fixed inset-0 overflow-hidden ${overlayClassName}`}
          style={{ zIndex: PORTAL_Z_OVERLAY }}
          data-modal-ignore-outside
          aria-hidden
          onMouseDown={handleOverlayPointerDown}
          onWheel={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
        />
        {panelNode}
      </>
    ) : null;

  if (variant === "inline") {
    return <div className={`w-full ${className}`}>{panelNode}</div>;
  }

  return (
    <div className={`relative min-w-0 ${className}`}>
      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={triggerAriaLabel}
        tabIndex={disabled ? -1 : 0}
        className={`duration-trigger box-border flex min-w-0 min-h-0 overflow-hidden cursor-pointer select-none items-center rounded-[6px] border border-solid border-border-gray dark:border-border-gray-dk bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] transition-colors focus:outline-none
          ${disabled && "opacity-80 "}
          ${triggerClassName}
        `}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        {renderTrigger()}
      </div>

      {isOpen &&
        (effectiveUsePortal
          ? createPortal(portalSubtree, document.body)
          : panelNode)}
    </div>
  );
};

export default DurationPopper;
